import { Box, Button, Typography } from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { useTranslation } from "react-i18next";
import { Dayjs } from "dayjs";
import {
  colorBorder,
  colorSurface,
  colorTextMuted,
  colorTextSecondary,
  colorSuccess,
  colorDealBg,
  colorDealBorder,
  colorDealText,
  radiusCard,
} from "src/design/tokens";
import CustomCalendar from "./CustomCalendar";
import { PublicDeal } from "src/data/clientBooking/queries/clientBooking";


interface Props {
  selectedDate: Dayjs | null;
  onSelectDate: (date: Dayjs) => void;
  onConfirmDate: () => void;
  availableDates: string[];
  specialDealDates: string[];
  applicableDeals: PublicDeal[];
}

const DateSelection = ({
  selectedDate,
  onSelectDate,
  onConfirmDate,
  availableDates,
  specialDealDates,
  applicableDeals,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        {t("clientBooking.selectDate")}
      </Typography>
      <Typography variant="body2" color={colorTextMuted} sx={{ mb: 2 }}>
        {t("clientBooking.selectDateSubtitle")}
      </Typography>

      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            border: `1px solid ${colorBorder}`,
            borderRadius: "20px",
            px: 1.25,
            py: 0.5,
            backgroundColor: colorSurface,
          }}
        >
          <CircleIcon sx={{ fontSize: 10, color: colorSuccess }} />
          <Typography variant="body2" fontSize={12} color={colorTextSecondary}>
            {t("clientBooking.available")}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            border: `1px solid ${colorBorder}`,
            borderRadius: "20px",
            px: 1.25,
            py: 0.5,
            backgroundColor: colorSurface,
          }}
        >
          <LocalOfferOutlinedIcon sx={{ fontSize: 12, color: colorDealText }} />
          <Typography variant="body2" fontSize={12} color={colorTextSecondary}>
            {t("clientBooking.specialDeal")}
          </Typography>
        </Box>
      </Box>

      <CustomCalendar
        selectedDate={selectedDate}
        onSelectDate={onSelectDate}
        availableDates={availableDates}
        specialDealDates={specialDealDates}
      />

      {applicableDeals.length > 0 && (
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          {applicableDeals.map((deal) => (
            <Box
              key={deal.id}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.25,
                backgroundColor: colorDealBg,
                border: `1px solid ${colorDealBorder}`,
                borderRadius: radiusCard,
                p: 1.5,
              }}
            >
              <LocalOfferOutlinedIcon
                sx={{ fontSize: 16, color: colorDealText, mt: 0.1, flexShrink: 0 }}
              />
              <Typography variant="body2" color={colorDealText}>
                {deal.description}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {selectedDate && (
        <Button
          variant="contained"
          fullWidth
          onClick={onConfirmDate}
          sx={{ mt: 2 }}
        >
          {t("clientBooking.continueCta")}
        </Button>
      )}
    </Box>
  );
};

export default DateSelection;
